import { useCreateProject } from '@/hooks/useProject';
import { useToast } from '@/hooks/useToast';
import {
  ProjectFormData,
  ProjectSchema,
  TourOperatorProjectFormData,
  TourOperatorProjectSchema,
} from '@/types/projects/ProjectFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Control,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import Modal from '../ui/Modal';
import StyledInput from '../styled/StyledInput';
import { Label } from '../ui/label';
import StyledButton from '../styled/StyledButton';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useCallback } from 'react';
import StyledDatePicker from '../styled/StyledDatePicker';
import { useTranslation } from '@/i18n';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';

interface CreateProjectModalProps {
  clientId: string;
  closeModal: () => void;
  onSuccess: () => void;
  isTourOperator?: boolean;
}

const CreateProjectModal = ({
  clientId,
  closeModal,
  onSuccess,
  isTourOperator: isTourOperatorProp,
}: CreateProjectModalProps) => {
  const { t } = useTranslation();
  const { isTourOperator: isTourOperatorFromMe, tProject } =
    useDistributorLabels();
  const isTourOperator = isTourOperatorProp ?? isTourOperatorFromMe;

  const agencyForm = useForm<TourOperatorProjectFormData>({
    mode: 'onChange',
    resolver: zodResolver(TourOperatorProjectSchema),
    defaultValues: {
      name: undefined,
      clientFirstName: undefined,
      adults: [],
      children: [],
    },
  });

  const projectForm = useForm<ProjectFormData>({
    mode: 'onChange',
    resolver: zodResolver(ProjectSchema),
    defaultValues: {
      name: undefined,
      adults: [],
      children: [],
    },
  });

  const { mutateAsync: createProjectData } = useCreateProject(clientId);
  const { toastAsync } = useToast();

  const submitAgencyForm: SubmitHandler<TourOperatorProjectFormData> = async (
    data,
  ) => {
    const { status } = await toastAsync(createProjectData(data), {
      success: tProject('projectCreatedSuccess'),
    });
    if (status < 300) {
      closeModal();
      onSuccess();
    }
  };

  const submitProjectForm: SubmitHandler<ProjectFormData> = async (data) => {
    const { status } = await toastAsync(createProjectData(data), {
      success: t('project.projectCreatedSuccess'),
    });
    if (status < 300) {
      closeModal();
      onSuccess();
    }
  };

  if (isTourOperator) {
    const {
      control,
      handleSubmit,
      formState: { isValid },
    } = agencyForm;

    return (
      <Modal
        title={tProject('newProject')}
        actionTitle={t('common.next')}
        actionOnClick={handleSubmit(submitAgencyForm)}
        isActionDisabled={!isValid}
        closeModal={closeModal}
        message={t('common.requiredFieldsNote')}
      >
        <div className="flex flex-col gap-6 w-full">
          <StyledInput
            control={control}
            name="name"
            title={tProject('name')}
            placeholder={tProject('namePlaceholder')}
          />
          <StyledInput
            control={control}
            name="clientFirstName"
            title={tProject('firstName')}
            placeholder={tProject('firstNamePlaceholder')}
          />
          <div className="max-h-[600px] 2xl:max-h-[700px] overflow-y-auto">
            <ParticipantsList control={control as Control<ProjectFormData>} />
          </div>
        </div>
      </Modal>
    );
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = projectForm;

  return (
    <Modal
      title={t('project.newProject')}
      actionTitle={t('common.next')}
      actionOnClick={handleSubmit(submitProjectForm)}
      isActionDisabled={!isValid}
      closeModal={closeModal}
      message={t('common.requiredFieldsNote')}
    >
      <div className="flex flex-col gap-6 w-full">
        <StyledInput
          control={control}
          name="name"
          title={t('project.name')}
          placeholder={t('project.namePlaceholder')}
        />
        <div className="max-h-[600px] 2xl:max-h-[700px] overflow-y-auto">
          <ParticipantsList control={control as Control<ProjectFormData>} />
        </div>
      </div>
    </Modal>
  );
};

export default CreateProjectModal;

interface ParticipantsListProps {
  control: Control<ProjectFormData>;
  isViewMode?: boolean;
}

export const ParticipantsList = ({
  control,
  isViewMode = false,
}: ParticipantsListProps) => {
  const { t } = useTranslation();

  const {
    fields: adults,
    append: appendAdult,
    remove: removeAdult,
  } = useFieldArray({
    control,
    name: 'adults',
  });

  const {
    fields: children,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({
    control,
    name: 'children',
  });

  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        <Label className="text-gray-700">{t('project.addParticipants')}</Label>
        <div className="flex flex-row w-full items-center gap-6">
          <div className="flex flex-row gap-9 w-full items-center">
            <span className="text-sm text-gray-800">{t('project.adults')}</span>
            <div className="flex gap-6 w-full items-center">
              <StyledButton
                variant="link"
                className="p-0 border border-gray-300 size-8"
                title=""
                icon={<MinusIcon className="size-5" />}
                onClick={() => removeAdult(adults.length - 1)}
                disabled={adults.length === 0 || isViewMode}
              />
              <span className="text-sm w-3 text-black text-center">
                {adults.length}
              </span>
              <StyledButton
                variant="link"
                className="p-0 border border-gray-300 size-8"
                title=""
                icon={<PlusIcon className="size-5" />}
                onClick={() => appendAdult({ firstName: '', lastName: '' })}
                disabled={isViewMode}
              />
            </div>
          </div>
          <div className="flex flex-row gap-9 w-full items-center">
            <span className="text-sm text-gray-800">
              {t('project.children')}
            </span>
            <div className="flex gap-6 w-full items-center">
              <StyledButton
                variant="link"
                className="p-0 border border-gray-300 size-8"
                title=""
                icon={<MinusIcon className="size-5" />}
                onClick={() => removeChild(children.length - 1)}
                disabled={children.length === 0 || isViewMode}
              />
              <span className="text-sm w-3 text-black text-center">
                {children.length}
              </span>
              <StyledButton
                variant="link"
                className="p-0 border border-gray-300 size-8"
                title=""
                icon={<PlusIcon className="size-5" />}
                onClick={() =>
                  appendChild({
                    firstName: '',
                    lastName: '',
                    birthDate: undefined,
                  })
                }
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
      </div>
      {adults.map((adult, index) => (
        <ParticipantForm
          key={adult.id}
          control={control}
          index={index}
          type="adults"
          isViewMode={isViewMode}
        />
      ))}
      {children.map((child, index) => (
        <ParticipantForm
          key={child.id}
          control={control}
          index={index}
          type="children"
          isViewMode={isViewMode}
        />
      ))}
    </>
  );
};

interface ParticipantFormProps {
  control: Control<ProjectFormData>;
  index: number;
  type: 'adults' | 'children';
  isViewMode?: boolean;
}

const ParticipantForm = ({
  control,
  index,
  type,
  isViewMode = false,
}: ParticipantFormProps) => {
  const { t } = useTranslation();

  const getLabel = useCallback(
    (input: 'Nom' | 'Prénom' | 'Date de naissance') => {
      if (type === 'adults')
        return `${t('project.participantForm.adult', { index: index + 1 })} - ${input}`;
      return `${t('project.participantForm.child', { index: index + 1 })} - ${input}`;
    },
    [index, type, t],
  );

  return (
    <div className="flex flex-row gap-4 w-full">
      <StyledInput
        control={control}
        name={`${type}.${index}.lastName`}
        title={getLabel('Nom')}
        className="w-full"
        disabled={isViewMode}
      />
      <StyledInput
        control={control}
        name={`${type}.${index}.firstName`}
        title={getLabel('Prénom')}
        className="w-full"
        disabled={isViewMode}
      />
      {type === 'children' && (
        <div className="flex flex-col gap-1.5 w-full">
          <Label className="text-gray-700">
            {getLabel('Date de naissance')} *
          </Label>
          <StyledDatePicker
            control={control}
            name={`${type}.${index}.birthDate`}
            className="w-full"
            acceptOnlyPastDates
            isViewMode={isViewMode}
          />
        </div>
      )}
    </div>
  );
};
